import React, { ReactElement, useState, useRef, useEffect, MutableRefObject } from 'react';

import historyStyles from './AlmeHistory.styles';
import { debounce } from '@material-ui/core/utils';
import { Theme, Container } from '@material-ui/core';
import { createStyles, makeStyles, WithStyles, withStyles, useTheme } from '@material-ui/core/styles';
import { FocusComponentProps } from '../../models/ComponentProps';
import AlmeScrollButton from '../AlmeScrollButton/AlmeScrollButton';
import { AlmeHistoryConfig } from './AlmeHistoryConfig';
import { AlmeResponseNext } from '@alme/types/response/AlmeResponse';
import AlmeResponse from '../AlmeResponse/AlmeResponse';
import AlmeLiveChatResponse from '../LiveChat/AlmeLiveChatResponse/AlmeLiveChatResponse';
import { DisplayLink } from '@alme/types/response/DisplayLink';
import { CtaResponseAction } from '../ResponseActions/AlmeCtaButton/AlmeCtaButton';
import { isLiveChatResponse } from '@alme/api-ts/Utils';
import { inIframe } from '../../utils/iFrameUtils';
import { FireSurveyAppEvent } from "../ResponseActions/AlmeSurvey/AlmeSurvey";
import AlmeTimeoutWarning from '../AlmeTimeoutWarning/AlmeTimeoutWarning';
import { NavUrl } from '@alme/types/response/NavUrl';
import { LiveChatNotificationResponseNext } from '@alme/types/response/LiveChatNotificationResponse';
import { useIntl } from 'react-intl';
import { CookieLoader } from '@alme/storage-ts/CookieLoader';

const scrollPositionLoader = new CookieLoader<string>('scrollPosition');


const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        root: {
            overflow: 'auto',
            padding: theme.spacing(1),
            marginBottom: '2px',
        },
    }),
);

interface AlmeHistoryProps extends WithStyles<typeof historyStyles> {
    scrollToResponse: MutableRefObject<boolean>;
    history: AlmeResponseNext[];
    onDisplayLinkClick: (link: DisplayLink | NavUrl) => void;
    onSurveySubmitClick: FireSurveyAppEvent;
    setIsDialogOpen: (isOpen: boolean) => void;
    onCtaClick: (action: CtaResponseAction, entryId: string) => Promise<void>;
    almeConfig: AlmeHistoryConfig;
    shouldDisplayTimeout: boolean;
    isDialogOpen?: boolean;
    focusProps?: FocusComponentProps;
    isLiveChatConnected?: boolean;
    setShouldDisplayTimeout: (newState: boolean) => void;
}

function AlmeHistory(props: AlmeHistoryProps): ReactElement {
    const intl = useIntl();
    const classes = useStyles();
    const theme = useTheme();
    const [isVisible, setIsVisible] = useState<boolean>(false);
    const [renderControlFlag, setRenderControlFlag] = useState<boolean>(false);
    const historyRef = useRef<HTMLDivElement>(null);

    const handleScroll = (): void => {
        if (!historyRef?.current?.children || historyRef.current.children.length <= 0) {
            return;
        }
        const scrollHeight = historyRef.current.scrollHeight;
        const scrollTop = historyRef.current.scrollTop;
        const offsetHeight = historyRef.current.offsetHeight;
        if (renderControlFlag) {
            props.scrollToResponse.current = false;
            scrollPositionLoader.UpdateStorage(scrollTop.toString());
        }


        const lastChild = historyRef.current.children[historyRef.current.children.length - 1];
        let childHeight = lastChild.clientHeight - theme.spacing(2);
        if (childHeight < 1) {
            childHeight = 0;
        }

        const shouldBeVisible = scrollTop < (scrollHeight - offsetHeight - childHeight);
        if ((isVisible && shouldBeVisible) || (!isVisible && !shouldBeVisible)) {
            return;
        }
        setIsVisible(shouldBeVisible);
    };

    const onScrollClick = () => {
        if (!historyRef?.current?.children || historyRef.current.children.length <= 0) {
            return;
        }
        setIsVisible(false);

        const scrollHeight = historyRef.current.scrollHeight;
        const lastChild = historyRef.current.children[historyRef.current.children.length - 1] as HTMLDivElement;

        if (!lastChild.clientHeight) {
            return;
        }
        let childHeight = lastChild.offsetHeight + theme.spacing(1);
        if (childHeight < 1) {
            childHeight = 0;
        }

        const childTop = (scrollHeight - childHeight);

        if (props.scrollToResponse.current) {
            if (inIframe()) {
                historyRef.current.scrollTop = childTop;
            }
            else if (lastChild?.scrollIntoView) {
                lastChild.scrollIntoView({ behavior: 'smooth' });
            }
            props.scrollToResponse.current = false;
            scrollPositionLoader.UpdateStorage(childTop.toString());
        }
    };

    useEffect(() => {
        handleScroll();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (historyRef.current) {
            //Navigation, scroll to bottom
            if (props.scrollToResponse.current) {
                onScrollClick();
                setRenderControlFlag(true);
            }
            else {
                const scroll = scrollPositionLoader.GetStorage();

                historyRef.current.scrollTop = scroll ? parseInt(scroll) : historyRef.current.scrollTop;

                setRenderControlFlag(true);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [historyRef.current, setRenderControlFlag]);

    const accessibilityAttrs = {
        "aria-live": 'polite' as const,
        role: 'dialog',
    };
    return (
        <div className={props.classes.box} >
            <Container
                id='alme-history'
                aria-label={
                    intl.formatMessage({
                        id: "history.historyAriaLabel",
                        defaultMessage: 'Default history aria label',
                        description: 'AlmeHistory aria label',
                    })}
                disableGutters
                className={`${classes.root} ${props.classes.root}`}
                {...props.focusProps}
                ref={historyRef}
                onScroll={debounce(handleScroll, 500)}
                {...accessibilityAttrs}
            >
                {props.history?.length > 0 && props.history.map((response, i) => {
                    if (isLiveChatResponse(response)) {
                        const prevResponse = (i > 0) ? props.history[i - 1] as LiveChatNotificationResponseNext : undefined;
                        return (
                            <AlmeLiveChatResponse
                                response={response as LiveChatNotificationResponseNext}
                                isNewResponse={isNewResponse(response as LiveChatNotificationResponseNext, prevResponse)}
                                key={i}
                                responseId={i + 1}
                                isLastResponse={i === props.history.length - 1}
                                almeConfig={props.almeConfig.history}
                                focusProps={{ disabled: props.isDialogOpen }}
                                onScroll={onScrollClick}
                                onDisplayLinkClick={props.onDisplayLinkClick}
                            />
                        );
                    }
                    return (
                        <AlmeResponse
                            response={response}
                            key={i}
                            responseId={i + 1}
                            onDisplayLinkClick={props.onDisplayLinkClick}
                            onSurveySubmitClick={props.onSurveySubmitClick}
                            isLastResponse={i === props.history.length - 1}
                            almeConfig={props.almeConfig.history}
                            setIsDialogOpen={props.setIsDialogOpen}
                            focusProps={{ disabled: props.isDialogOpen || props.isLiveChatConnected, tabIndex: props.focusProps?.tabIndex}}
                            isDialogOpen={props.isDialogOpen}
                            onCtaClick={props.onCtaClick}
                            onScroll={onScrollClick}
                            scrollToResponse={props.scrollToResponse}
                        />
                    );
                })}
            </Container>
            {isVisible &&
                <AlmeScrollButton almeConfig={props.almeConfig.history} onScrollClick={onScrollClick} scrollToResponse={props.scrollToResponse} focusProps={props.focusProps} />}
            <AlmeTimeoutWarning
                almeConfig={props.almeConfig}
                shouldDisplayTimeout={props.shouldDisplayTimeout}
                focusProps={props.focusProps}
                color={props.almeConfig.history.timeout.styles.backgroundColor}
                setShouldDisplayTimeout={props.setShouldDisplayTimeout}
            />
        </div >
    );
}

export default withStyles(historyStyles)(AlmeHistory);

export function isNewResponse(response: LiveChatNotificationResponseNext, prevResponse?: LiveChatNotificationResponseNext): boolean {
    const isTransient = response?.extensionData?.Transient === true;
    const isPrevTransient = prevResponse?.extensionData?.Transient === true;
    return !isTransient || !isPrevTransient || response.text !== prevResponse?.text;
}
